import React, { useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  Typography,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import { Close, DirectionsCar } from "@mui/icons-material";
import { useMutation } from "react-query";
import { addVehicleForSdvFeature } from "../../services/SdvFeature";
import { useToast } from "../../components/toast";
import VehicleListForSdvDataGrid from "../sdv-feature/VehicleListForSdvFeature";

const SdvFeatureVehicleMap = ({ open, toggleDialog, sdvData }) => {
  const { addToast } = useToast();
  const [selectedVehiclesList, setSelectedVehiclesList] = useState([]);
  const { sdvFeatureName, sdvFeatureId } = sdvData;

  const { mutate: mutateVehicleForSdvFeature, isLoading } = useMutation(
    addVehicleForSdvFeature,
    {
      onSuccess: async () => {
        toggleDialog();
        addToast({
          type: "success",
          message: `Vehicles successfully added to ${sdvFeatureName}.`,
          autoClose: 3000,
        });
      },
      onError: (e) => {
        addToast({
          type: "error",
          message: e?.response?.data?.message || "Failed to add vehicles",
          autoClose: 3000,
        });
      },
    },
  );

  const handleSubmit = () => {
    mutateVehicleForSdvFeature({
      sdvFeatureId,
      serialNo: selectedVehiclesList,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={toggleDialog}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "85%",
          maxWidth: "100px",
          minHeight: "560px",
          borderRadius: 1,
          border: "0.5px solid",
          borderColor: "divider",
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 2.5, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "#E6F1FB",
                color: "#185FA5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DirectionsCar fontSize="small" />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "text.primary",
                  lineHeight: 1.3,
                }}
              >
                Map vehicles to feature
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.25 }}
              >
                {sdvFeatureName}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {selectedVehiclesList.length > 0 && (
              <Chip
                label={`${selectedVehiclesList.length} selected`}
                size="small"
                sx={{
                  bgcolor: "#E6F1FB",
                  color: "#185FA5",
                  fontWeight: 500,
                  fontSize: "12px",
                  height: 24,
                }}
              />
            )}
            <IconButton
              onClick={toggleDialog}
              size="small"
              aria-label="Close dialog"
              sx={{
                border: "0.5px solid",
                borderColor: "divider",
                borderRadius: 1,
                color: "text.secondary",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ height: "0.5px", bgcolor: "divider", mt: 2 }} />
      </DialogTitle>

      <DialogContent
        sx={{
          px: 0,
          pt: 0,
          pb: 0,
          flex: 1,
          overflowY: "auto",
          "& .MuiDataGrid-root": {
            border: "none",
            borderRadius: 0,
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "background.default",
            borderBottom: "0.5px solid",
            borderColor: "divider",
            fontSize: "11px",
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.secondary",
          },
          "& .MuiDataGrid-row": {
            fontSize: "13px",
            "&:hover": { bgcolor: "action.hover" },
          },
          "& .MuiDataGrid-cell": {
            borderColor: "divider",
            color: "text.primary",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "0.5px solid",
            borderColor: "divider",
            fontSize: "13px",
          },
          "& .MuiCheckbox-root": {
            color: "text.disabled",
            "&.Mui-checked": { color: "#185FA5" },
          },
        }}
      >
        <VehicleListForSdvDataGrid
          sdvData={sdvData}
          setSelectedVehiclesList={setSelectedVehiclesList}
          selectedVehiclesList={selectedVehiclesList}
        />
      </DialogContent>

      <Box sx={{ height: "0.5px", bgcolor: "divider" }} />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {selectedVehiclesList.length > 0 && (
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", flex: 1 }}
          >
            {selectedVehiclesList.length} vehicle
            {selectedVehiclesList.length !== 1 ? "s" : ""} selected
          </Typography>
        )}
        <Button
          onClick={toggleDialog}
          variant="outlined"
          size="small"
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: "13px",
            borderColor: "divider",
            color: "text.secondary",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={!selectedVehiclesList.length || isLoading}
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 500,
            bgcolor: "#1a3c8f",
            "&:hover": { bgcolor: "#142e6e" },
            "&.Mui-disabled": { opacity: 0.5 },
            minWidth: 100,
          }}
        >
          {isLoading
            ? "Adding..."
            : `Add vehicle${selectedVehiclesList.length !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SdvFeatureVehicleMap;
